import type { Directive, DirectiveBinding } from 'vue';
import { getObserverOptions, getLowQualityImageUrl, isSameSrc, isValidImageUrl } from './utils';

const stateMap = new WeakMap<
	HTMLImageElement,
	{
		src: string;
		observer: IntersectionObserver | null;
		loader?: () => void;
	}
>();

// 清理函数
const cleanup = (el: HTMLImageElement) => {
	const state = stateMap.get(el);
	if (state) {
		if (state.observer) {
			state.observer.disconnect();
		}
		if (state.loader) {
			state.loader();
		}
	}
	stateMap.delete(el);
};

// 加载图片逻辑
const loadImage = (el: HTMLImageElement, src: string) => {
	// 设置低质量占位图
	el.src = getLowQualityImageUrl(src);

	const observer = new IntersectionObserver((entries) => {
		if (entries[0].isIntersecting) {
			const img = new Image();
			const onLoad = () => {
				el.src = src;
				el.dispatchEvent(new Event('load'));
				cleanup(el);
			};

			const onError = (e: Event) => {
				// 阻止错误冒泡到全局
				e.stopPropagation();
				e.preventDefault();
				// 加载失败，仍然清理
				cleanup(el);
			};

			img.addEventListener('load', onLoad, { once: true });
			img.addEventListener('error', onError, { once: true, capture: true });
			img.src = src;

			const state = stateMap.get(el);
			if (state) {
				state.loader = () => {
					img.removeEventListener('load', onLoad);
					img.removeEventListener('error', onError);
					img.src = '';
				};
			}
		}
	}, getObserverOptions());

	observer.observe(el);

	stateMap.set(el, { src, observer });
};

const handleLazyImage = (el: HTMLImageElement, binding: DirectiveBinding) => {
	const isEnabled = binding.value !== false;
	const newSrc = el.src || el.getAttribute('data-src') || '';

	// 严格验证 URL
	if (!isValidImageUrl(newSrc)) {
		el.removeAttribute('src');
		cleanup(el);
		return;
	}

	const state = stateMap.get(el);

	if (state && isSameSrc(state.src, newSrc) && isEnabled) {
		return;
	}

	cleanup(el);

	if (!isEnabled) {
		return;
	}

	loadImage(el, newSrc);
};

const lazyImage: Directive = {
	mounted: handleLazyImage,
	updated: handleLazyImage,
	unmounted: cleanup
};

export default lazyImage;
